import { Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ProductInferredFact } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

type ProductInferredFactsCardProps = {
  inferredFacts: ProductInferredFact[]
  onEditFact: (fact: ProductInferredFact) => void
  onDeleteFact: (fact: ProductInferredFact) => void
}

export function ProductInferredFactsCard({
  inferredFacts,
  onEditFact,
  onDeleteFact,
}: ProductInferredFactsCardProps) {
  const { t } = useTranslation('productCatalog')
  const hasFacts = inferredFacts.length > 0

  return (
    <Card className='h-fit'>
      <CardHeader className='flex flex-row items-center justify-between gap-3 pb-4'>
        <CardTitle className='text-base leading-tight'>
          {t('detail.inferredFacts.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        {!hasFacts && (
          <p className='text-sm text-muted-foreground'>
            {t('detail.inferredFacts.empty')}
          </p>
        )}
        {hasFacts && (
          <div className='max-h-96 overflow-y-auto pe-3'>
            <div className='space-y-3'>
              {inferredFacts.map((fact) => (
                <div
                  key={fact.id}
                  className='rounded-md border border-border/60 p-3 text-sm'
                >
                  <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0 space-y-1'>
                      <p className='font-medium wrap-break-word text-foreground'>
                        {fact.label}
                      </p>
                      <p className='wrap-break-word text-muted-foreground'>
                        {fact.value}
                      </p>
                    </div>
                    <div className='flex shrink-0 items-center'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='size-8 text-muted-foreground hover:text-foreground'
                        aria-label={t('detail.inferredFacts.editAriaLabel', {
                          label: fact.label,
                        })}
                        onClick={() => onEditFact(fact)}
                      >
                        <Pencil className='size-4' aria-hidden />
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='size-8 text-muted-foreground hover:text-destructive'
                        aria-label={t('detail.inferredFacts.deleteAriaLabel', {
                          label: fact.label,
                        })}
                        onClick={() => onDeleteFact(fact)}
                      >
                        <Trash2 className='size-4' aria-hidden />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
